import {type UserRole} from './roles';

/**
 * Session type (previously from next-auth, now defined locally)
 */
export type Session = {
  user: {
    id: string;
    email: string;
    name: string;
    role: UserRole;
    tenantId: string;
    image?: string;
  };
  expires?: string;
};

/**
 * Enhanced authentication state interface
 */
export type AuthState = {
  /** Whether authentication status is currently being determined */
  isLoading: boolean;
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean;
  /** The authenticated user's information, undefined if not authenticated */
  user: AuthUser | undefined;
  /** The complete session object from NextAuth */
  session: Session | undefined;
};

/**
 * MATIC user information with custom attributes
 */
export type AuthUser = {
  /** Unique user identifier */
  id: string;
  /** User's email address */
  email: string;
  /** User's display name */
  name: string;
  /** User's profile image URL (optional) */
  image?: string;
  /** MATIC tenant identifier */
  tenantId: string;
  /** User's role within the tenant */
  role: UserRole;
};

/**
 * Route protection configuration
 */
export type RouteProtectionConfig = {
  /** Routes that don't require authentication */
  publicRoutes: string[];
  /** Routes that require authentication */
  protectedRoutes: string[];
  /** Default redirect path for unauthenticated users */
  defaultRedirect: string;
  /** Whether to preserve the intended destination URL */
  preserveCallback: boolean;
};

/**
 * Authentication mode enumeration
 */
export enum AuthMode {
  /** Local mock authentication for development */
  LOCAL = 'local',
  /** AWS Cognito authentication */
  COGNITO = 'cognito',
}

/**
 * Authentication error types
 */
export type AuthError = {
  /** Error code */
  code: string;
  /** Human-readable error message */
  message: string;
  /** Additional error details */
  details?: Record<string, any>;
};

/**
 * Protected route component props
 */
export type ProtectedRouteProps = {
  /** Child components to render when authenticated */
  children: React.ReactNode;
  /** Custom redirect path (defaults to home page) */
  redirectTo?: string;
  /** Whether to preserve callback URL in redirect */
  preserveCallback?: boolean;
  /** Required user role for access */
  requiredRole?: string;
  /** Multiple acceptable roles for access */
  requiredRoles?: string[];
};

/**
 * Auth guard component props
 */
export type AuthGuardProps = {
  /** Child components to render when authenticated */
  children: React.ReactNode;
  /** Custom fallback component for unauthenticated users (defaults to redirect to /login) */
  fallback?: React.ReactNode;
  /** Required user role for access */
  requiredRole?: string;
  /** Multiple acceptable roles for access */
  requiredRoles?: string[];
};

/**
 * Route matching utility types
 */
export type RoutePattern = string | RegExp;

/**
 * Middleware configuration
 */
export type MiddlewareConfig = {
  /** Patterns for routes that should be processed by middleware */
  matcher: string[];
  /** Routes to exclude from middleware processing */
  excludePatterns: RoutePattern[];
};

/**
 * Environment detection result
 */
export type EnvironmentConfig = {
  /** Current authentication mode */
  authMode: AuthMode;
  /** Whether running in development mode */
  isDevelopment: boolean;
  /** Current MATIC environment */
  maticEnv: string;
};

/**
 * Token validation result
 */
export type TokenValidationResult = {
  /** Whether the token is valid */
  isValid: boolean;
  /** The validated user information */
  user?: AuthUser;
  /** Validation error if token is invalid */
  error?: AuthError;
};

/**
 * Authentication hook return type
 */
export type UseAuthReturn = AuthState;

/**
 * Sign-in options
 */
export type SignInOptions = {
  /** OAuth provider to use */
  provider?: string;
  /** Callback URL after successful sign-in */
  callbackUrl?: string;
  /** Additional parameters to pass to the provider */
  params?: Record<string, string>;
};

/**
 * Role-based access control utilities
 */
export type RoleCheck = {
  /** Check if user has specific role */
  hasRole: (user: AuthUser | undefined, role: string) => boolean;
  /** Check if user has any of the specified roles */
  hasAnyRole: (user: AuthUser | undefined, roles: string[]) => boolean;
  /** Check if user has all of the specified roles */
  hasAllRoles: (user: AuthUser | undefined, roles: string[]) => boolean;
};

/**
 * Loading state types
 */
export type LoadingState = 'idle' | 'loading' | 'success' | 'error';

/**
 * Authentication loading component props
 */
export type AuthLoadingProps = {
  /** Loading state type */
  state?: LoadingState;
  /** Custom loading message */
  message?: string;
  /** Whether to show skeleton loader */
  showSkeleton?: boolean;
  /** Custom CSS classes */
  className?: string;
};
